import { useEffect, useRef, useState } from "react";

export interface UseGridListboxOption {
  value: string;
}

export interface UseGridListboxArgs<T extends UseGridListboxOption> {
  id: string;
  options: T[];
  value?: string;
  onChange: (value: string) => void;
  columns: number;
  disabled?: boolean;
}

export function useGridListbox<T extends UseGridListboxOption>({
  id,
  options,
  value,
  onChange,
  columns,
  disabled = false,
}: UseGridListboxArgs<T>) {
  const selectedIndex = options.findIndex((option) => option.value === value);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(selectedIndex >= 0 ? selectedIndex : 0);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const listboxId = `${id}-grid`;
  const optionId = (index: number) => `${listboxId}-option-${index}`;

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) optionRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [isOpen, highlightedIndex]);

  const commitSelection = (index: number) => {
    const option = options[index];
    if (!option) return;
    onChange(option.value);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const openAt = (index: number) => {
    setHighlightedIndex(Math.max(0, Math.min(index, options.length - 1)));
    setIsOpen(true);
  };

  const move = (delta: number) => {
    setHighlightedIndex((i) => Math.max(0, Math.min(i + delta, options.length - 1)));
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    switch (event.key) {
      case "ArrowRight":
        event.preventDefault();
        isOpen ? move(1) : openAt(selectedIndex >= 0 ? selectedIndex : 0);
        break;
      case "ArrowLeft":
        event.preventDefault();
        if (isOpen) move(-1);
        break;
      case "ArrowDown":
        event.preventDefault();
        isOpen ? move(columns) : openAt(selectedIndex >= 0 ? selectedIndex : 0);
        break;
      case "ArrowUp":
        event.preventDefault();
        if (isOpen) move(-columns);
        break;
      case "Home":
        if (isOpen) {
          event.preventDefault();
          setHighlightedIndex(0);
        }
        break;
      case "End":
        if (isOpen) {
          event.preventDefault();
          setHighlightedIndex(options.length - 1);
        }
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        isOpen ? commitSelection(highlightedIndex) : openAt(selectedIndex >= 0 ? selectedIndex : 0);
        break;
      case "Escape":
        if (isOpen) {
          event.preventDefault();
          setIsOpen(false);
        }
        break;
      case "Tab":
        setIsOpen(false);
        break;
    }
  };

  return {
    isOpen,
    setIsOpen,
    highlightedIndex,
    selectedIndex,
    containerRef,
    triggerRef,
    optionRefs,
    listboxId,
    optionId,
    openAt,
    commitSelection,
    handleTriggerKeyDown,
  };
}
